import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, type DomainDetail, type DomainLinks } from '../../../api';
import { Badge } from '../../../components/Badge';
import { DataTable, Section, type Column } from '../../../components/blocks';
import { useOpsReason } from '../../../api/opsReason';
import { DataError, OpsReasonBar, ReasonPrompt } from '../../../components/DataError';
import { CorrelationLink } from '../../../components/Ids';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { correlationLogQuery, logsLink } from '../../../lib/opsQuery';
import { useLabels } from '../../../lib/labels';
import { useApi } from '../../../lib/useApi';
import { TxRef, WorkflowRef } from '../refs';
import { COMMAND_STATE, humanize, LIFECYCLE, StatusBadge, WORKFLOW_STATE } from '../status';

type Workflow = DomainLinks['workflows'][number];
type Tx = DomainLinks['registry_transactions'][number];
type Evt = DomainLinks['recent_events'][number];

type Lookup = (key: string, options: { defaultValue: string }) => string;

/**
 * Operational context for one domain: workflows, registry commands, open reconciliation
 * issues and the correlation IDs to follow into the Correlation view, logs and traces (spec §116).
 */
export function ObservabilityTab({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const name = detail.domain.ascii_name;
  // The query names the domain: the Observability API answers only with a reason (spec §122), so nothing is sent before one.
  const reason = useOpsReason();
  const links = useApi((signal) => (reason ? api.getDomainLinks(name, { signal }) : Promise.resolve(null)), [name, reason]);
  const latestCorrelation = links.status === 'success' && links.data ? (links.data.recent_events.find((e) => e.correlation_id)?.correlation_id ?? null) : null;

  const wfColumns: Column<Workflow>[] = [
    { key: 'updated', header: t('observability.updated'), className: 'whitespace-nowrap', cell: (w) => formatDateTime(w.updated_at) },
    { key: 'wf', header: t('observability.workflow'), cell: (w) => <WorkflowRef id={w.id} type={w.workflow_type} sub={null} /> },
    { key: 'state', header: t('observability.state'), cell: (w) => <StatusBadge map={WORKFLOW_STATE} value={w.state} /> },
    { key: 'step', header: t('observability.step'), cell: (w) => <span className="text-sm">{L.workflowStep(w.current_step)}</span> },
    { key: 'corr', header: t('observability.correlation'), cell: (w) => <CorrelationLink id={w.correlation_id} /> },
  ];
  const txColumns: Column<Tx>[] = [
    { key: 'started', header: t('observability.started'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.started_at) },
    { key: 'op', header: t('observability.operation'), cell: (x) => <TxRef id={x.id} operation={x.operation} /> },
    { key: 'state', header: t('observability.state'), cell: (x) => <StatusBadge map={COMMAND_STATE} value={x.state} /> },
    { key: 'corr', header: t('observability.correlation'), cell: (x) => <CorrelationLink id={x.correlation_id} /> },
  ];
  const evColumns: Column<Evt>[] = [
    { key: 'seq', header: '#', numeric: true, cell: (e) => e.sequence },
    { key: 'when', header: t('observability.when'), className: 'whitespace-nowrap', cell: (e) => formatDateTime(e.occurred_at) },
    { key: 'event', header: t('observability.event'), cell: (e) => <span className="text-sm">{L.eventType(e.event_type)}</span> },
    { key: 'corr', header: t('observability.correlation'), cell: (e) => <CorrelationLink id={e.correlation_id} /> },
  ];

  return (
    <div className="grid grid-cols-1 gap-6">
      {reason ? <OpsReasonBar /> : <ReasonPrompt onSubmitted={links.reload} />}
      <Section title={t('observability.jump')} description={t('observability.jumpDescription')}>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" className="h-auto max-w-full min-h-8 py-1 text-left whitespace-normal">
            <Link to={logsLink(`{service=~".+"} |= "${name}"`, '24h')}>
              {t('observability.logsMentioning', { name })} <ArrowRight />
            </Link>
          </Button>
          {latestCorrelation ? (
            <Button asChild variant="outline" size="sm" className="h-auto max-w-full min-h-8 py-1 text-left whitespace-normal">
              <Link to={logsLink(correlationLogQuery(latestCorrelation), '7d')}>
                {t('observability.latestLogs')} <ArrowRight />
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" size="sm" className="h-auto max-w-full min-h-8 py-1 text-left whitespace-normal">
            <Link to={`/ops/registries?registry=${encodeURIComponent(detail.domain.registry)}`}>
              {t('observability.registryHealth', { registry: detail.domain.registry_name || detail.domain.registry })} <ArrowRight />
            </Link>
          </Button>
        </div>
      </Section>

      {reason && links.status === 'loading' ? <Loading label={t('observability.loading')} rows={3} /> : null}
      {links.status === 'error' ? <DataError error={links.error} onRetry={links.reload} title={t('observability.loadFailed')} /> : null}
      {links.status === 'success' && links.data ? (
        <>
          <Section title={t('observability.issues')} actions={<StatusBadge map={LIFECYCLE} value={links.data.lifecycle_state} />}>
            {links.data.open_issues.length ? (
              <ul className="divide-y">
                {links.data.open_issues.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center gap-2 py-2 text-sm first:pt-0 last:pb-0">
                    <Badge tone={i.severity === 'HIGH' || i.severity === 'CRITICAL' ? 'crit' : 'warn'} title={i.severity}>
                      {(t as unknown as Lookup)(`observability.severities.${i.severity}`, { defaultValue: humanize(i.severity) })}
                    </Badge>
                    <span>{(t as unknown as Lookup)(`observability.issueTypes.${i.issue_type}`, { defaultValue: humanize(i.issue_type) })}</span>
                    <span className="text-xs text-muted-foreground">{t('observability.detected', { when: formatRelative(i.detected_at) })}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="size-4 text-success" aria-hidden="true" /> {t('observability.noIssues')}
              </p>
            )}
          </Section>

          <Section flush title={t('observability.workflows')}>
            <DataTable columns={wfColumns} rows={links.data.workflows} rowKey={(w) => w.id} empty={<EmptyState title={t('observability.noWorkflows')} />} />
          </Section>

          <Section flush title={t('observability.txs')}>
            <DataTable columns={txColumns} rows={links.data.registry_transactions} rowKey={(x) => x.id} empty={<EmptyState title={t('observability.noTxs')} />} />
          </Section>

          <Section flush title={t('observability.events')}>
            <DataTable columns={evColumns} rows={links.data.recent_events} rowKey={(e) => String(e.sequence)} empty={<EmptyState title={t('observability.noEvents')} />} />
          </Section>
          <p className="text-xs text-muted-foreground">{t('observability.audited')}</p>
        </>
      ) : null}
    </div>
  );
}
